import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Settings, Lock, Eye, EyeOff, User, Mail, Phone, MapPin, BookOpen, Pencil, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`);
      setProfileData(response.data);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      toast.error("Failed to load profile information");
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = () => {
    if (profileData?.role === 'student') {
      setEditData({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        parent_name: profileData.parent_name || '',
        parent_phone: profileData.phone || '',
        home_address: profileData.address || '',
      });
    } else if (profileData?.role === 'teacher') {
      setEditData({
        full_name: profileData.full_name || '',
        phone_number: profileData.phone || '',
        physical_address: profileData.address || '',
        national_id: profileData.national_id || '',
        date_of_birth: profileData.date_of_birth ? profileData.date_of_birth.split('T')[0] : '',
        spouse_name: profileData.spouse_name || '',
        spouse_phone: profileData.spouse_phone || '',
      });
    }
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, editData);
      toast.success("Profile updated successfully");
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-3">
          <Settings className="text-pink-500" size={28} />
          Settings
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Manage your account settings and profile information
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Info */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <User size={20} className="text-pink-500" />
                  Account Information
                </CardTitle>
                <CardDescription>Your account and profile details</CardDescription>
              </div>
              {(profileData?.role === 'student' || profileData?.role === 'teacher') && !isEditing && (
                <Button variant="outline" size="sm" onClick={startEditing} className="text-slate-500 hover:text-pink-600">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
              )}
              {isEditing && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={isSaving}>
                    <X size={14} className="mr-1" /> Cancel
                  </Button>
                  <Button size="sm" className="bg-pink-500 hover:bg-pink-600" onClick={handleSaveProfile} disabled={isSaving}>
                    <Save size={14} className="mr-1" /> {isSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500 text-sm">Username</Label>
                <p className="font-medium text-slate-900">{profileData?.username}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-sm">Role</Label>
                <p className="font-medium text-slate-900 capitalize">{profileData?.role}</p>
              </div>

              {/* Display role-specific information */}
              {profileData?.role === 'student' && (
                <>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <User size={14} /> First Name
                    </Label>
                    {isEditing ? (
                      <Input value={editData.first_name} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.first_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <User size={14} /> Last Name
                    </Label>
                    {isEditing ? (
                      <Input value={editData.last_name} onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.last_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <BookOpen size={14} /> Class
                    </Label>
                    <p className="font-medium text-slate-900">{profileData?.class_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <Mail size={14} /> Parent Email
                    </Label>
                    <p className="font-medium text-slate-900">{profileData?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <User size={14} /> Parent Name
                    </Label>
                    {isEditing ? (
                      <Input value={editData.parent_name} onChange={(e) => setEditData({ ...editData, parent_name: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.parent_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <Phone size={14} /> Parent Phone
                    </Label>
                    {isEditing ? (
                      <Input value={editData.parent_phone} onChange={(e) => setEditData({ ...editData, parent_phone: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.phone || 'N/A'}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <MapPin size={14} /> Home Address
                    </Label>
                    {isEditing ? (
                      <Input value={editData.home_address} onChange={(e) => setEditData({ ...editData, home_address: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.address || 'N/A'}</p>
                    )}
                  </div>
                </>
              )}

              {profileData?.role === 'teacher' && (
                <>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <User size={14} /> Full Name
                    </Label>
                    {isEditing ? (
                      <Input value={editData.full_name} onChange={(e) => setEditData({ ...editData, full_name: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.full_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <BookOpen size={14} /> Assigned Class
                    </Label>
                    <p className="font-medium text-slate-900">{profileData?.class_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <Mail size={14} /> Email
                    </Label>
                    <p className="font-medium text-slate-900">{profileData?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <Phone size={14} /> Phone
                    </Label>
                    {isEditing ? (
                      <Input value={editData.phone_number} onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.phone || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <User size={14} /> National ID
                    </Label>
                    {isEditing ? (
                      <Input value={editData.national_id} onChange={(e) => setEditData({ ...editData, national_id: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.national_id || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      Date of Birth
                    </Label>
                    {isEditing ? (
                      <Input type="date" value={editData.date_of_birth} onChange={(e) => setEditData({ ...editData, date_of_birth: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.date_of_birth ? new Date(profileData.date_of_birth).toLocaleDateString() : 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      Spouse Name
                    </Label>
                    {isEditing ? (
                      <Input value={editData.spouse_name} onChange={(e) => setEditData({ ...editData, spouse_name: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.spouse_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <Phone size={14} /> Spouse Phone
                    </Label>
                    {isEditing ? (
                      <Input value={editData.spouse_phone} onChange={(e) => setEditData({ ...editData, spouse_phone: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.spouse_phone || 'N/A'}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-500 text-sm flex items-center gap-1">
                      <MapPin size={14} /> Address
                    </Label>
                    {isEditing ? (
                      <Input value={editData.physical_address} onChange={(e) => setEditData({ ...editData, physical_address: e.target.value })} className="mt-1" />
                    ) : (
                      <p className="font-medium text-slate-900">{profileData?.address || 'N/A'}</p>
                    )}
                  </div>
                </>
              )}

              {profileData?.role === 'admin' && (
                <div className="md:col-span-2">
                  <Label className="text-slate-500 text-sm">Account Created</Label>
                  <p className="font-medium text-slate-900">
                    {profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Lock size={18} className="text-pink-500" />
              Change Password
            </CardTitle>
            <CardDescription>Update your password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password" className="text-sm">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    className="pr-10 text-sm"
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    className="pr-10 text-sm"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-sm">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="text-sm"
                  data-testid="confirm-password"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-pink-500 hover:bg-pink-600"
                data-testid="change-password-btn"
              >
                {isLoading ? "Changing..." : "Change Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
